import { ArrowDownTrayIcon, ExclamationTriangleIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline"
import { useDispatch } from "react-redux";
import { partialUpdateForecastPlanningRequest } from "../../slices/quickbooks/forecastPlanningSlice";
import { monthsLowerCase, monthsUpperCase } from "../../consts/forecast"
import { formatPrice, getVarianceColor } from "../../helpers/common";
import { exportChannelBreakdownToCSV } from "../../helpers/planning/exports";
import { useEffect, useRef, useState } from "react";
import { OTBProductSelect } from "../otb/OTBProductSelect";
import { monthKeysForecastEnum } from "../../consts/quickbooksReports";
import { DropdownChannelsSettings } from "./DropdownChannelsSettings";

export const ChannelBreakdown = ({
    channelsBreakdown,
    forecastPlanningId,
    lastType,
    groupsBySelect,
    setChannelsBreakdown,
    mix
}) => {
    const channels = ["shopify", "amazon", "wholesale"];
    const dispatch = useDispatch();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [planPercentages, setPlanPercentages] = useState({});
    const [planPercentagesInputByChannel, setPlanPercentagesInputByChannel] = useState({});
    const [initialPlanPercentages, setInitialPlanPercentages] = useState({});
    const [disabledProducts, setDisabledProducts] = useState({});
    const [initialDisabledProducts, setInitialDisabledProducts] = useState({});
    const [addProductOpenByChannel, setAddProductOpenByChannel] = useState({});
    const [selectedProductByChannel, setSelectedProductByChannel] = useState({});
    const actionsBtnRefs = useRef({});
    const [openedActionsByRow, setOpenedActionsByRow] = useState({
        id: "",
        opened: false
    });
    // Initialize or recalculate plan percentages from saved breakdown (fallback to mix defaults only when no saved)
    useEffect(() => {
        const initial = {};
        const disabledInit = {};
        channels.forEach((channel) => {
            const savedProducts = channelsBreakdown?.[channel]?.products || {};
            const savedIds = Object.keys(savedProducts);
            const fromSaved = savedIds.reduce((acc, productId) => {
                const v = Number(savedProducts[productId]?.input_value ?? 0);
                acc[productId] = isNaN(v) ? 0 : v;
                return acc;
            }, {});
            const fromSavedDisabled = savedIds.reduce((acc, productId) => {
                acc[productId] = Boolean(savedProducts[productId]?.disabled || false);
                return acc;
            }, {});
            if (Object.keys(fromSaved).length === 0) {
                (mix || []).forEach((p) => {
                    fromSaved[p.id] = Number(p.pctRev || 0);
                    fromSavedDisabled[p.id] = false;
                    // default settings persisted for new rows
                    const base = channelsBreakdown || {};
                    const next = { ...base };
                    next[channel] = next[channel] || { products: {}, totals: {} };
                    next[channel].products = next[channel].products || {};
                    next[channel].products[p.id] = next[channel].products[p.id] || {};
                    next[channel].products[p.id].settings = {
                        start_month: "jan",
                        end_month: "dec",
                        ...(next[channel].products[p.id].settings || {}),
                    };
                    setChannelsBreakdown(next);
                });
            }
            initial[channel] = fromSaved;
            disabledInit[channel] = fromSavedDisabled;
        });
        setInitialPlanPercentages(initial);
        setInitialDisabledProducts(disabledInit);
        // If user has no unsaved edits, sync current to initial
        if (!hasUnsavedChanges) {
                                setPlanPercentages(initial);
            setDisabledProducts(disabledInit);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelsBreakdown]);

    useEffect(() => {
        if (!Array.isArray(mix)) return;
        if (!planPercentages || Object.keys(planPercentages || {}).length === 0) return;
        const next = buildBreakdownFromPlan();
        setChannelsBreakdown(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mix]);

    const buildBreakdownFromPlan = (baseChannels = null) => {
        const settingsSource = baseChannels || channelsBreakdown || {};
        const next = {};
        channels.forEach((channel) => {
            const products = {};
            (mix || []).forEach((product) => {
                const data = calculateChannelProductData(channel, product, settingsSource);
                const inputVal = Number(planPercentages?.[channel]?.[product.id] ?? 0);
                const isDisabled = Boolean(disabledProducts?.[channel]?.[product.id] || false);
                products[product.id] = {
                    input_value: inputVal,
                    disabled: isDisabled,
                    settings: {
                        start_month: "jan",
                        end_month: "dec",
                        ...(settingsSource?.[channel]?.products?.[product.id]?.settings || {}),
                    },
                    annual: {
                        actual: Number(data.annual_actual || 0),
                        forecast: Number(data.annual_forecast || 0),
                        variance: Number(data.annual_variance || 0),
                    },
                    months: {
                        actual: { ...(data.monthly_actuals_by_month || {}) },
                        forecast: { ...(data.monthly_forecast_by_month || {}) },
                        variance: { ...(data.monthly_variance_by_month || {}) },
                    },
                };
            });
            const totals = getChannelTotals(channel, settingsSource);
            next[channel] = {
                products,
                totals: {
                    annual: {
                        actual: Number(totals.annual_actual || 0),
                        forecast: Number(totals.annual_forecast || 0),
                        variance: Number(totals.annual_variance || 0),
                    },
                    months: {
                        actual: { ...(totals.monthly_actuals_by_month || {}) },
                        forecast: { ...(totals.monthly_forecast_by_month || {}) },
                        variance: { ...(totals.monthly_variance_by_month || {}) },
                    },
                },
            };
        });
        return next;
    };

    const fillMissingPlanPercentages = () => {
        const filled = {};
        channels.forEach((channel) => {
            const current = planPercentages?.[channel] || {};
            const withAll = { ...current };
            (mix || []).forEach((p) => {
                if (withAll[p.id] == null) {
                    withAll[p.id] = 0;
                }
            });
            filled[channel] = withAll;
        });
        return filled;
    };

    const mapSavedToInternal = (saved) => {
        return {
            annual_actual: Number(saved?.annual?.actual || 0),
            annual_forecast: Number(saved?.annual?.forecast || 0),
            annual_variance: Number(saved?.annual?.variance ?? (Number(saved?.annual?.actual || 0) - Number(saved?.annual?.forecast || 0))),
            monthly_actuals_by_month: saved?.months?.actual || {},
            monthly_forecast_by_month: saved?.months?.forecast || {},
            monthly_variance_by_month: saved?.months?.variance || {},
        };
    };

    const toMonthIndex = (keyOrNumber) => {
        if (typeof keyOrNumber === "number") return keyOrNumber - 1;
        const idx = (monthsLowerCase || []).indexOf(String(keyOrNumber || "").toLowerCase());
        return idx; // -1 if not found
    };

    const calculateChannelProductData = (channel, product, settingsSource) => {
        const isDisabled = Boolean(disabledProducts?.[channel]?.[product.id] || false);
        if (isDisabled) {
            const zeros = (monthsLowerCase || []).reduce((acc, m) => ({ ...acc, [m]: 0 }), {});
            return {
                annual_actual: 0,
                annual_forecast: 0,
                annual_variance: 0,
                monthly_actuals_by_month: zeros,
                monthly_forecast_by_month: zeros,
                monthly_variance_by_month: zeros,
            };
        }
        const p = (Number(planPercentages[channel]?.[product.id] || 0)) / 100;
        const settings = settingsSource?.[channel]?.products?.[product.id]?.settings || {};
        const startIdx = Math.max(0, toMonthIndex(settings.start_month ?? "jan") ?? 0);
        const endIdx = Math.min(11, toMonthIndex(settings.end_month ?? "dec") ?? 11);
        const isActive = (i) => i >= startIdx && i <= endIdx;

        const monthlyActuals = (monthsLowerCase || []).reduce((acc, m, i) => {
            const base = Number(product?.monthly_actuals_by_month?.[m] || 0) * p;
            acc[m] = isActive(i) ? base : 0;
            return acc;
        }, {});
        const monthlyForecast = (monthsLowerCase || []).reduce((acc, m, i) => {
            const base = Number(product?.monthly_forecast_by_month?.[m] || 0) * p;
            acc[m] = isActive(i) ? base : 0;
            return acc;
        }, {});
        const annualActual = Object.values(monthlyActuals).reduce((s, v) => s + Number(v || 0), 0);
        const annualForecast = Object.values(monthlyForecast).reduce((s, v) => s + Number(v || 0), 0);
        const monthlyVariance = (monthsLowerCase || []).reduce((acc, m) => {
            acc[m] = Number(monthlyActuals[m] || 0) - Number(monthlyForecast[m] || 0);
            return acc;
        }, {});
        const annualVariance = Number(annualActual || 0) - Number(annualForecast || 0);

        return {
            annual_actual: annualActual,
            annual_forecast: annualForecast,
            annual_variance: annualVariance,
            monthly_actuals_by_month: monthlyActuals,
            monthly_forecast_by_month: monthlyForecast,
            monthly_variance_by_month: monthlyVariance,
        };
    };

    const getChannelTotals = (channel, settingsSource) => {
        const baseMonthTotals = (monthsLowerCase || []).reduce((acc, m) => ({ ...acc, [m]: 0 }), {});
        const totals = {
            annual_actual: 0,
            annual_forecast: 0,
            annual_variance: 0,
            monthly_actuals_by_month: { ...baseMonthTotals },
            monthly_forecast_by_month: { ...baseMonthTotals },
            monthly_variance_by_month: { ...baseMonthTotals },
        };
        (mix || []).forEach((product) => {
            const isDisabled = Boolean(disabledProducts?.[channel]?.[product.id] || false);
            if (isDisabled) return;
            const data = calculateChannelProductData(channel, product, settingsSource);
            totals.annual_actual += Number(data.annual_actual || 0);
            totals.annual_forecast += Number(data.annual_forecast || 0);
            (monthsLowerCase || []).forEach((m) => {
                totals.monthly_actuals_by_month[m] += Number(data.monthly_actuals_by_month?.[m] || 0);
                totals.monthly_forecast_by_month[m] += Number(data.monthly_forecast_by_month?.[m] || 0);
            });
        });
        totals.annual_variance = totals.annual_actual - totals.annual_forecast;
        (monthsLowerCase || []).forEach((m) => {
            totals.monthly_variance_by_month[m] = Number(totals.monthly_actuals_by_month[m] || 0) - Number(totals.monthly_forecast_by_month[m] || 0);
        });
        return totals;
    };
    const validatePlanPercentages = (channel) => {
        const entries = Object.entries(planPercentages[channel] || {});
        const total = entries.reduce((sum, [productId, pct]) => {
            const isDisabled = Boolean(disabledProducts?.[channel]?.[productId]);
            return isDisabled ? sum : sum + Number(pct || 0);
        }, 0);
        const roundedTotal = Math.round((total + Number.EPSILON) * 100) / 100;
        return roundedTotal >= 100;
    };
    const isAllChannelsValid = () => {
        return channels.every((channel) => validatePlanPercentages(channel));
    };
    const updatePlanPercentage = (channel, productId, value) => {
        setPlanPercentages((prev) => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                [productId]: value,
            },
        }));
        setHasUnsavedChanges(true);
    };

    const toggleDisabled = (channel, productId) => {
        setDisabledProducts((prev) => ({
            ...prev,
            [channel]: {
                ...prev[channel],
                [productId]: !Boolean(prev?.[channel]?.[productId] || false),
            }
        }));
        setHasUnsavedChanges(true);
    };

    const getChannelPlanSum = (channel) => {
        let total = 0;
    
        if (hasUnsavedChanges) {
            const entries = Object.entries(planPercentages?.[channel] || {});
            total = entries.reduce((sum, [productId, v]) => {
                const isDisabled = Boolean(disabledProducts?.[channel]?.[productId]);
                return isDisabled ? sum : sum + Number(v || 0);
            }, 0);
        } else {
            const savedProducts = channelsBreakdown?.[channel]?.products || {};
            total = Object.values(savedProducts).reduce(
                (sum, p) => p?.disabled ? sum : sum + Number(p?.input_value || 0),
                0
            );
        }
    
        return Math.round((total + Number.EPSILON) * 100) / 100;
    };
    

    const exportChannelToCSV = (channelKey) => exportChannelBreakdownToCSV({ channelKey, channelsBreakdown, hasUnsavedChanges, planPercentages, mix, groupsBySelect, disabledProducts });
    return (
        <div className="min-w-0">
            <h2 className="text-xl font-semibold mb-4">
                Channel/Product Breakdown
            </h2>
            <div className="space-y-6">
                {!isAllChannelsValid() && (
                    <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex gap-2 items-center border-red-200 bg-red-50">
                        <ExclamationTriangleIcon className="lucide lucide-triangle-alert h-4 w-4"/>
                        <div className="col-start-2 grid justify-items-start gap-1 text-sm">
                            Plan % ≠ 100% in one or more channels. Adjust Plan % values to total 100% for each channel.
                        </div>
                    </div>
                )}
                <div className="grid gap-6">
                    {channels.map((key) => {
                        const savedTotals = channelsBreakdown?.[key]?.totals;
                        const shouldUseSaved = !hasUnsavedChanges;
                        const channelTotals = shouldUseSaved && savedTotals ? mapSavedToInternal(savedTotals) : getChannelTotals(key, channelsBreakdown);
                        return (
                            <div key={key} className="bg-[#f5f5f5] flex flex-col gap-6 rounded-xl border py-6 shadow-sm overflow-x-auto pb-2">
                                <div className="grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="leading-none font-semibold">
                                                {key.charAt(0).toUpperCase() + key.slice(1)}
                                            </div>
                                            {(() => {
                                                const sum = getChannelPlanSum(key);
                                                return (
                                                    <span className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-medium border-blue-700 text-blue-700 bg-blue-100`}>
                                                        {Number(sum || 0)}% of revenue
                                                    </span>
                                                );
                                            })()}
                                            {!validatePlanPercentages(key) && (
                                                <span className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-medium border-red-300 text-red-700 bg-red-50">
                                                    Plan % ≠ 100%
                                                </span>
                                            )}
                                        </div>
                                        <button onClick={() => exportChannelToCSV(key)} className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none border bg-white shadow-xs h-8 rounded-md gap-1.5 px-3 hover:bg-black hover:text-white">
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                            <span>Export CSV</span>
                                        </button>
                                    </div>
                                </div>
                                <div className="px-6 min-w-0">
                                    <div className="overflow-x-auto w-full">
                                        <div className="min-w-fit">
                                            <div className="min-w-[500px] grid shrink-0 [grid-template-columns:minmax(160px,220px)_96px_72px_repeat(13,minmax(132px,1fr))] gap-2 text-sm">
                                                <div className="font-medium text-gray-700 py-2">
                                                    Product
                                                </div>
                                                <div className="font-medium text-gray-500 py-2 text-center opacity-70 cursor-help border-r border-gray-200">
                                                    LY %
                                                </div>
                                                <div className="font-medium text-gray-700 py-2 text-center">
                                                    Plan %
                                                </div>
                                                <div className="font-medium text-gray-700 py-2">
                                                    Annual
                                                </div>
                                                {monthsUpperCase.map((month) => {
                                                    return (
                                                        <div key={month} className="font-medium text-gray-700 py-2 text-center">
                                                            {month}
                                                        </div>
                                                    )
                                                })}
                                                {(mix || []).map((mixItem) => {
                                                    const savedProduct = channelsBreakdown?.[key]?.products?.[mixItem.id];
                                                    const isDisabled = shouldUseSaved
                                                        ? Boolean(savedProduct?.disabled || false)
                                                        : Boolean(disabledProducts?.[key]?.[mixItem.id] || false);
                                                    if (isDisabled) return null;
                                                    const data = (shouldUseSaved && savedProduct) ? mapSavedToInternal(savedProduct) : calculateChannelProductData(key, mixItem, channelsBreakdown);
                                                    return (
                                                        <>
                                                            <div key={mixItem?.id} className="contents">
                                                                <div className="flex flex-col gap-2">
                                                                    <div className="py-2 flex flex-row gap-4 items-center justify-between">
                                                                        <div className="text-base font-bold">
                                                                            {(groupsBySelect || []).find((group) => group.value === mixItem.id)?.label}
                                                                        </div>
                                                                        {/* Disable via button click */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => toggleDisabled(key, mixItem.id)}
                                                                            className="p-1 rounded flex hover:bg-gray-100"
                                                                            aria-label={isDisabled ? "Enable product" : "Disable product"}
                                                                        >
                                                                            <XMarkIcon className="h-5 w-5 text-black cursor-pointer" />
                                                                        </button>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => {
                                                                            setOpenedActionsByRow((prev) => {
                                                                                const isSame = prev.id === `${key}-${mixItem.id}` && prev.opened;
                                                                                return { id: `${key}-${mixItem.id}`, opened: !isSame };
                                                                            });
                                                                        }}
                                                                        ref={(el) => { actionsBtnRefs.current[`${key}-${mixItem.id}`] = el; }}
                                                                        className="inline-flex w-fit items-center rounded bg-black text-white px-5 py-1 text-xs font-medium hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/40"
                                                                    >
                                                                        {channelsBreakdown?.[key]?.products?.[mixItem.id]?.settings?.start_month ? `Start: ${monthKeysForecastEnum[channelsBreakdown?.[key]?.products?.[mixItem.id]?.settings?.start_month || "jan"] || ""}` : "jan"}
                                                                    </button>
                                                                    <DropdownChannelsSettings 
                                                                        isOpen={openedActionsByRow?.opened && openedActionsByRow?.id === `${key}-${mixItem.id}`}
                                                                        forecastProduct={{
                                                                            start_month: channelsBreakdown?.[key]?.products?.[mixItem.id]?.settings?.start_month || "jan",
                                                                            end_month: channelsBreakdown?.[key]?.products?.[mixItem.id]?.settings?.end_month || "dec",
                                                                        }}
                                                                        cancelClick={() => {
                                                                            setOpenedActionsByRow({
                                                                                id: "",
                                                                                opened: false
                                                                            })
                                                                        }}
                                                                        onChangeSettings={(changes) => {
                                                                            const base = channelsBreakdown || {};
                                                                            const next = { ...base };
                                                                            next[key] = next[key] || { products: {}, totals: {} };
                                                                            next[key].products = next[key].products || {};
                                                                            next[key].products[mixItem.id] = next[key].products[mixItem.id] || {};
                                                                            next[key].products[mixItem.id].settings = {
                                                                                start_month: "jan",
                                                                                end_month: "dec",
                                                                                ...(next[key].products[mixItem.id].settings || {}),
                                                                                ...changes,
                                                                            };
                                                                            // recompute with new settings
                                                                            const recomputed = buildBreakdownFromPlan(next);
                                                                            setChannelsBreakdown(recomputed);
                                                                            setHasUnsavedChanges(true);
                                                                        }}
                                                                        anchorEl={actionsBtnRefs.current[`${key}-${mixItem.id}`]}
                                                                        usePortal
                                                                    />
                                                                </div>
                                                                <div className="py-2 text-center text-sm border-r border-gray-100">
                                                                    <span className="text-gray-500 opacity-70 font-medium">
                                                                        LY%
                                                                    </span>
                                                                </div>
                                                                <div className="py-2">
                                                                    <input
                                                                        className="border-input h-9 min-w-0 rounded-md border bg-transparent px-3 py-1 shadow-xs outline-none file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full text-center text-sm"
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        placeholder="0.00"
                                                                        value={(planPercentagesInputByChannel?.[key]?.[mixItem.id]) ?? String(Number(planPercentages?.[key]?.[mixItem.id] ?? 0))}
                                                                        onChange={(e) => {
                                                                            let value = String(e.target.value ?? "");
                                                                            // allow digits and a single dot
                                                                            value = value.replace(/[^0-9.]/g, "");
                                                                            const firstDot = value.indexOf(".");
                                                                            if (firstDot !== -1) {
                                                                                const before = value.slice(0, firstDot + 1);
                                                                                const after = value.slice(firstDot + 1).replace(/\./g, "");
                                                                                value = before + after;
                                                                            }
                                                                            // limit to two decimals
                                                                            if (value.includes(".")) {
                                                                                const [i, d = ""] = value.split(".");
                                                                                value = `${i}.${d.slice(0, 2)}`;
                                                                            }
                                                                            // trim leading zeros on integer part and limit to 3 digits
                                                                            let [intPart = "", decPart] = value.split(".");
                                                                            intPart = intPart.replace(/^0+(?=\d)/, "");
                                                                            intPart = intPart.slice(0, 3);
                                                                            value = decPart !== undefined ? `${intPart || "0"}.${decPart}` : intPart;

                                                                            const parsed = parseFloat(value);
                                                                            if (!isNaN(parsed)) {
                                                                                const clamped = Math.max(0, Math.min(100, parsed));
                                                                                if (clamped !== parsed) {
                                                                                    value = String(clamped);
                                                                                }
                                                                                updatePlanPercentage(key, mixItem.id, Number(clamped.toFixed(2)));
                                                                            }

                                                                            setPlanPercentagesInputByChannel((prev) => ({
                                                                                ...prev,
                                                                                [key]: {
                                                                                    ...(prev?.[key] || {}),
                                                                                    [mixItem.id]: value,
                                                                                }
                                                                            }));
                                                                        }}
                                                                        onBlur={() => {
                                                                            const fallback = String(Number(planPercentages?.[key]?.[mixItem.id] ?? 0));
                                                                            const raw = (planPercentagesInputByChannel?.[key]?.[mixItem.id] ?? fallback);
                                                                            const parsed = parseFloat(raw);
                                                                            const clampedNum = Math.max(0, Math.min(100, isNaN(parsed) ? 0 : parsed));
                                                                            const normalized = Number(clampedNum.toFixed(2));
                                                                            updatePlanPercentage(key, mixItem.id, normalized);
                                                                            setPlanPercentagesInputByChannel((prev) => {
                                                                                const next = { ...(prev || {}) };
                                                                                if (next?.[key]) {
                                                                                    next[key] = { ...(next[key] || {}) };
                                                                                    delete next[key][mixItem.id];
                                                                                }
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        disabled={isDisabled}
                                                                    />
                                                                </div>
                                                                {/* Annual Column */}
                                                                <div className="py-2 space-y-1">
                                                                    <div className="font-bold">
                                                                        ${formatPrice(Number(data?.annual_actual || 0))}
                                                                    </div>
                                                                    <div className="text-gray-500">
                                                                        ${formatPrice(Number(data?.annual_forecast || 0))}
                                                                    </div>
                                                                    <div className={`font-medium ${getVarianceColor(Number(data?.annual_variance || 0))}`}>
                                                                        ${formatPrice(Number(data?.annual_variance || 0))}
                                                                    </div>
                                                                </div>
                                                                {/* Monthly Columns */}
                                                                {monthsUpperCase.map((month, idx) => {
                                                                    const monthKey = monthsLowerCase[idx];
                                                                    const actual = Number(data?.monthly_actuals_by_month?.[monthKey] || 0);
                                                                    const forecast = Number(data?.monthly_forecast_by_month?.[monthKey] || 0);
                                                                    const variance = actual - forecast;
                                                                    return (
                                                                        <div key={month} className="py-2 text-center space-y-1">
                                                                            <div className="font-bold">
                                                                                ${formatPrice(actual)}
                                                                            </div>
                                                                            <div className="text-gray-500">
                                                                                ${formatPrice(forecast)}
                                                                            </div>
                                                                            <div className={`font-medium ${getVarianceColor(variance)}`}>
                                                                                ${formatPrice(variance)}
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </div>
                                                        </>
                                                    )
                                                })}

                                                {(() => {
                                                    const disabledOptions = (mix || [])
                                                        .filter((p) => Boolean(disabledProducts?.[key]?.[p.id] || false))
                                                        .map((p) => ({ value: p.id, label: (groupsBySelect || []).find((g) => g.value === p.id)?.label || p.id }));
                                                    const hasDisabled = disabledOptions.length > 0;
                                                    if (!hasDisabled) return null;
                                                    const addOpen = Boolean(addProductOpenByChannel?.[key]);
                                                    const selectedProduct = selectedProductByChannel?.[key] || null;
                                                    return (
                                                        <div className="contents">
                                                            <div className="flex w-[400px] gap-2 relative z-20">
                                                                {!addOpen && hasDisabled && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setAddProductOpenByChannel((prev) => ({ ...prev, [key]: true }));
                                                                        }}
                                                                        className="inline-flex items-center h-[38px] justify-center whitespace-nowrap text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] rounded-md gap-1.5 text-black hover:text-black hover:bg-gray-100"
                                                                    >
                                                                        <PlusIcon className="h-5 w-5" />
                                                                        Add Product
                                                                    </button>
                                                                )}
                                                                {addOpen && hasDisabled && (
                                                                    <>
                                                                        <div className={`min-w-[132px] w-full max-w-[260px] ${addOpen ? "h-auto" : "h-[38px]"} flex items-center gap-2 overflow-visible`}>
                                                                            <OTBProductSelect
                                                                                options={disabledOptions}
                                                                                selectedProduct={selectedProduct}
                                                                                handleChange={(val) => setSelectedProductByChannel((prev) => ({ ...prev, [key]: val }))}
                                                                                optionsIsLoading={false}
                                                                                placeholder="Select product"
                                                                                containerClassName="!w-full"
                                                                                buttonClassName="!bg-black !text-white"
                                                                                dropdownClassName="!shadow-xl !relative"
                                                                                optionActiveClassName="!bg-black !text-white"
                                                                            />
                                                                        </div>
                                                                        <div className={`col-start-3 ${addOpen ? "py-0" : "py-1"} flex items-start gap-1`}>
                                                                            <button
                                                                                onClick={() => {
                                                                                    if (!selectedProduct) return;
                                                                                    // enable the product
                                                                                    const pid = selectedProduct;
                                                                                    setDisabledProducts((prev) => ({
                                                                                        ...prev,
                                                                                        [key]: {
                                                                                            ...prev[key],
                                                                                            [pid]: false,
                                                                                        }
                                                                                    }));
                                                                                    setSelectedProductByChannel((prev) => ({ ...prev, [key]: null }));
                                                                                    setAddProductOpenByChannel((prev) => ({ ...prev, [key]: false }));
                                                                                    setHasUnsavedChanges(true);
                                                                                }}
                                                                                className="whitespace-nowrap text-sm font-medium transition-all outline-none shadow-xs gap-1.5 h-9 inline-flex items-center justify-center rounded-md bg-black text-white px-4 py-2 hover:bg-black"
                                                                            >
                                                                                Add
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setAddProductOpenByChannel((prev) => ({ ...prev, [key]: false }));
                                                                                    setSelectedProductByChannel((prev) => ({ ...prev, [key]: null }));
                                                                                }}
                                                                                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all rounded-md gap-1.5 px-3 h-9"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                            <div className="relative z-10" />
                                                            <div className="relative z-10" />
                                                            <div className="relative z-10" />
                                                            {monthsUpperCase.map((month, idx) => {
                                                                return <div key={`${month}_${idx}`} className="relative z-10" />
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                                {/* Totals Row */}
                                                <div className="py-2 font-bold border-t border-gray-300 mt-2">
                                                    TOTAL
                                                </div>
                                                <div className="py-2 text-center border-t border-gray-300 mt-2 border-r border-gray-100">
                                                    <span className="text-gray-500 opacity-70 font-medium">
                                                        LY%
                                                    </span>
                                                </div>
                                                <div className="py-2 text-center border-t border-gray-300 mt-2">
                                                    {(() => {
                                                        const sum = getChannelPlanSum(key);
                                                        const isOk = sum >= 100;
                                                        const cls = isOk ? "text-green-600" : "text-red-500";
                                                        return (
                                                            <span className={`font-bold ${cls}`}>
                                                                {Number(sum || 0)}%
                                                            </span>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="py-2 space-y-1 border-t border-gray-300 mt-2">
                                                    <div className="font-bold">
                                                        ${formatPrice(Number(channelTotals?.annual_actual || 0))}
                                                    </div>
                                                    <div className="text-gray-500">
                                                        ${formatPrice(Number(channelTotals?.annual_forecast || 0))}
                                                    </div>
                                                    <div className={`font-medium ${getVarianceColor(Number(channelTotals?.annual_variance || 0))}`}>
                                                        ${formatPrice(Number(channelTotals?.annual_variance || 0))}
                                                    </div>
                                                </div>
                                                {monthsUpperCase.map((month, idx) => {
                                                    const monthKey = monthsLowerCase[idx];
                                                    const actual = Number(channelTotals?.monthly_actuals_by_month?.[monthKey] || 0);
                                                    const forecast = Number(channelTotals?.monthly_forecast_by_month?.[monthKey] || 0);
                                                    const variance = actual - forecast;
                                                    return (
                                                        <div key={month} className="py-2 text-center space-y-1 border-t border-gray-300 mt-2">
                                                            <div className="font-bold">
                                                                ${formatPrice(actual)}
                                                            </div>
                                                            <div className="text-gray-500">
                                                                ${formatPrice(forecast)}
                                                            </div>
                                                            <div className={`font-medium ${getVarianceColor(variance)}`}>
                                                                ${formatPrice(variance)}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className="flex justify-between items-center">
                    {hasUnsavedChanges && (
                        <div className="text-sm text-amber-600 font-medium">
                            ● Unsaved Changes        
                        </div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={() => {
                                setPlanPercentages(initialPlanPercentages);
                                setDisabledProducts(initialDisabledProducts);
                                setHasUnsavedChanges(false);
                            }}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none h-9 px-4 py-2 border border-gray-300 bg-white hover:bg-gray-50 text-gray-800"
                            disabled={!hasUnsavedChanges}
                        >
                            Reset
                        </button>
                        <button
                            onClick={async () => {
                                const filled = fillMissingPlanPercentages();
                                setPlanPercentages(filled);
                                const next = buildBreakdownFromPlan();
                                setChannelsBreakdown(next);
                                const initialFromSaved = {};
                                Object.keys(next || {}).forEach((channel) => {
                                    const productMap = next[channel]?.products || {};
                                    initialFromSaved[channel] = Object.keys(productMap).reduce((acc, id) => {
                                        acc[id] = Number(productMap[id]?.input_value || 0);
                                        return acc;
                                    }, {});
                                });
                                setInitialPlanPercentages(initialFromSaved);
                                const initialDisabledFromSaved = {};
                                Object.keys(next || {}).forEach((channel) => {
                                    const productMap = next[channel]?.products || {};
                                    initialDisabledFromSaved[channel] = Object.keys(productMap).reduce((acc, id) => {
                                        acc[id] = Boolean(productMap[id]?.disabled || false);
                                        return acc;
                                    }, {});
                                });
                                setInitialDisabledProducts(initialDisabledFromSaved);
                                setHasUnsavedChanges(false);
                                if (!forecastPlanningId) return;
                                await dispatch(partialUpdateForecastPlanningRequest({
                                    forecastPlanningId,
                                    lastType,
                                    scenarioChanges: { channels_breakdown: next },
                                }));
                            }}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 shrink-0 outline-none h-9 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={!hasUnsavedChanges}
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}